import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import InputField from "@/components/form/InputField";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import * as Yup from "yup";
import { apiKeyService } from "@/services/breeze/ApiKeyService";
import { ApiKeyResponse, CreateApiKeyRequest } from "@/types/breeze/ApiKey";
import { useMyPermissions } from "@/hooks/useCommonQueries";
import SelectField from "@/components/form/SelectField";
import { BasicTable } from "@/components/ui/BasicTable";
import { format } from "date-fns";
import { useState } from "react";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import Modal from "@/components/ui/Modal";
import { Text, Box, Button } from "@radix-ui/themes";
import Surface from "@/components/ui/Surface";

const apiKeySchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  expires_in_days: Yup.number()
    .required("Expiration days is required")
    .min(1, "Must be at least 1 day")
    .max(365, "Cannot exceed 365 days"),
  permission_ids: Yup.array()
    .of(Yup.number())
    .required("At least one permission is required"),
});

const ApiKeyManager = () => {
  const queryClient = useQueryClient();
  const { data: myPermissions } = useMyPermissions();
  const confirm = useConfirm();
  const [lastApiKey, setLastApiKey] = useState<string | undefined>(undefined);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKeyResponse | null>(
    null,
  );

  const {
    data: apiKeys,
    isLoading: isApiKeysLoading,
    error: apiKeysError,
  } = useQuery({
    queryKey: ["api-keys"],
    queryFn: async () => {
      return apiKeyService.getMyApiKeys();
    },
  });

  const { mutate: createApiKey, isPending: isCreatingApiKey } = useMutation({
    mutationFn: (data: CreateApiKeyRequest) => apiKeyService.createApiKey(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      setLastApiKey(data.key);
    },
  });

  const { mutate: revokeApiKey, isPending: isRevokingApiKey } = useMutation({
    mutationFn: (id: string) => apiKeyService.revokeApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });

  const handleRevokeApiKey = async (id: string) => {
    const confirmed = await confirm({
      title: "Revoke API Key",
      message: `Are you sure you want to revoke this API key?`,
      confirmText: "Revoke",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (confirmed) {
      revokeApiKey(id);
    }
  };

  return (
    <div>
      <div className="space-y-4">
        <Text size="4" className="font-bold block mb-2">
          API Keys
        </Text>
        <Text>
          API keys enable you to interact with Anythink's API programmatically.
          You can use them to make authenticated requests to access data and
          perform operations through the API. Keep your API keys secure and
          never expose them in public repositories or client-side code. If an
          API key is compromised, you must revoke it immediately and create a
          new one.
        </Text>

        <Formik
          initialValues={{
            name: "",
            expires_in_days: 30,
            permission_ids: [],
          }}
          validationSchema={apiKeySchema}
          onSubmit={(values, { resetForm }) => {
            createApiKey(values);
            resetForm();
          }}
        >
          <Form className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <InputField name="name" label="Name" required />
              <SelectField
                name="expires_in_days"
                label="Expires in (days)"
                required
                type="number"
              >
                <SelectField.Item value={30}>30 days</SelectField.Item>
                <SelectField.Item value={90}>90 days</SelectField.Item>
                <SelectField.Item value={180}>180 days</SelectField.Item>
                <SelectField.Item value={365}>365 days</SelectField.Item>
              </SelectField>
            </div>
            <SelectField
              name="permission_ids"
              label="Permissions"
              required
              type="number"
              options={{ multiple: true }}
            >
              <SelectField.Group label="Data Model Permissions">
                {myPermissions
                  ?.filter((p) => p.entity_id)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((permission) => (
                    <SelectField.Item
                      key={permission.id}
                      value={permission.id.toString()}
                    >
                      {permission.name}
                    </SelectField.Item>
                  ))}
              </SelectField.Group>

              <SelectField.Group label="Anythink Permissions">
                {myPermissions
                  ?.filter((p) => !p.entity_id)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((permission) => (
                    <SelectField.Item
                      key={permission.id}
                      value={permission.id.toString()}
                    >
                      {permission.name}
                    </SelectField.Item>
                  ))}
              </SelectField.Group>
            </SelectField>
            <Button type="submit" loading={isCreatingApiKey}>
              Create API Key
            </Button>
          </Form>
        </Formik>

        {isApiKeysLoading ? (
          <LoadingScreen fullPage={false} />
        ) : apiKeysError ? (
          <div>Error loading API keys</div>
        ) : (
          <div className="mt-8">
            {lastApiKey && (
              <div className="mb-8 p-4 border border-yellow-400 bg-yellow-50 rounded-md">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Copy your API key now. It will never be shown again!
                    </h3>
                    <div className="mt-2">
                      <code className="text-sm bg-yellow-100 px-2 py-1 rounded font-mono break-all">
                        {lastApiKey}
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <h4 className="text-lg font-semibold mb-4">Your API Keys</h4>
            <BasicTable
              columns={[
                { header: "Name", accessor: "name" },
                {
                  header: "Key",
                  accessor: "key",
                  cellRenderer: (row: ApiKeyResponse) => (
                    <span className="font-mono">
                      {row.key ? row.key : "••••••••••••••••"}
                    </span>
                  ),
                },
                {
                  header: "Expires",
                  accessor: "expires_at",
                  cellRenderer: (row: ApiKeyResponse) =>
                    format(new Date(row.expires_at), "MMM d, yyyy"),
                },
                {
                  header: "Status",
                  accessor: "revoked",
                  cellRenderer: (row: ApiKeyResponse) => (
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        row.revoked
                          ? "bg-red-100 text-red-800"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {row.revoked ? "Revoked" : "Active"}
                    </span>
                  ),
                },
                {
                  header: "Actions",
                  cellRenderer: (row: ApiKeyResponse) => (
                    <div className="space-x-2">
                      <Button
                        variant="ghost"
                        size="1"
                        onClick={() => setSelectedApiKey(row)}
                      >
                        View Permissions
                      </Button>
                      {!row.revoked && (
                        <Button
                          variant="ghost"
                          size="1"
                          onClick={() => handleRevokeApiKey(row.id)}
                          loading={isRevokingApiKey}
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  ),
                },
              ]}
              data={(apiKeys || []).sort(
                (a, b) => Number(a.revoked) - Number(b.revoked),
              )}
            />
          </div>
        )}
      </div>

      <Modal
        isOpen={!!selectedApiKey}
        onClose={() => setSelectedApiKey(null)}
        title={`Permissions for ${selectedApiKey?.name}`}
        size="md"
      >
        <div className="max-h-[60vh] overflow-y-auto">
          <div className="p-4 space-y-2">
            {selectedApiKey?.permissions.map((permission) => (
              <Surface
                key={permission.id}
                title={permission.name}
                body={permission.description}
                additionalTitleContent={
                  <Box>
                    <Text
                      size="1"
                      color={permission.is_active ? "green" : "red"}
                      style={{
                        backgroundColor: `var(--${
                          permission.is_active ? "green" : "red"
                        }-a2)`,
                        padding: "2px 8px",
                        borderRadius: "9999px",
                      }}
                    >
                      {permission.is_active ? "Active" : "Inactive"}
                    </Text>
                  </Box>
                }
              />
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ApiKeyManager;
